import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import './Auth.css';

const MIN_PASSWORD_LENGTH = 6; // must match the server rule

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  // 'checking' avoids flashing a password form for a link that is already dead.
  const [tokenState, setTokenState] = useState<'checking' | 'valid' | 'invalid'>('checking');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const validate = async () => {
      if (!token) {
        setTokenState('invalid');
        return;
      }
      try {
        const response = await authAPI.validateResetToken(token);
        if (!cancelled) setTokenState(response.data?.valid ? 'valid' : 'invalid');
      } catch {
        if (!cancelled) setTokenState('invalid');
      }
    };

    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setDone(true);
      // Give the confirmation a moment to register before moving on.
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not reset your password. Please try again.');
      // A rejected token will not start working, so send them back to the start.
      if (err.response?.status === 400) setTokenState('invalid');
    } finally {
      setLoading(false);
    }
  };

  if (tokenState === 'checking') {
    return (
      <div className="auth-container">
        <div className="auth-form">
          <h1>Reset Password</h1>
          <p className="auth-help">Checking your reset link...</p>
        </div>
      </div>
    );
  }

  if (tokenState === 'invalid') {
    return (
      <div className="auth-container">
        <div className="auth-form">
          <h1>Link Expired</h1>
          <div className="error">
            This password reset link is invalid or has expired. Reset links are only
            good for 60 minutes and can be used once.
          </div>
          <Link to="/forgot-password" className="auth-button auth-button-link">
            Request a New Link
          </Link>
          <p>
            <Link to="/login">Back to login</Link>
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="auth-container">
        <div className="auth-form">
          <h1>Password Updated</h1>
          <div className="success">
            Your password has been reset. Taking you to the login page...
          </div>
          <Link to="/login" className="auth-button auth-button-link">
            Log In Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h1>Set a New Password</h1>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="new-password">New Password:</label>
            <input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
              autoComplete="new-password"
              autoFocus
            />
            <small className="auth-hint">At least {MIN_PASSWORD_LENGTH} characters.</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirm New Password:</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>

          <button type="submit" disabled={loading} className="auth-button">
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        <p>
          <Link to="/login">Back to login</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
